// src/pages/Announcements.js
// Updated: Sunday, July 19, 2026 at 9:56 PM EDT

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { uploadFile, deleteFileByUrl } from '../services/storageService';

import AnnouncementCard from '../components/AnnouncementCard';
import AnnouncementModal from '../components/AnnouncementModal';
import { Button, Container, Typography, CircularProgress, Box, Divider, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';

function Announcements() {
  const { announcementId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const [scheduledAnnouncements, setScheduledAnnouncements] = useState([]);
  const [activeAnnouncements, setActiveAnnouncements] = useState([]);
  const [expiredAnnouncements, setExpiredAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readAnnouncements, setReadAnnouncements] = useState({});
  const [isChurchPushNotificationsEnabled, setIsChurchPushNotificationsEnabled] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  useEffect(() => {
    if (!userProfile?.churchId || !currentUser?.uid) {
      setLoading(false);
      return;
    }

    const churchDocRef = doc(db, 'churches', userProfile.churchId);
    const unsubscribeChurch = onSnapshot(churchDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setIsChurchPushNotificationsEnabled(docSnap.data().enablePushNotifications || false);
      } else {
        setIsChurchPushNotificationsEnabled(false);
      }
    });

    const announcementsColRef = collection(db, 'churches', userProfile.churchId, 'announcements');
    const q = query(announcementsColRef, orderBy('createdAt', 'desc'));
    
    const unsubscribeAnnouncements = onSnapshot(q, (snapshot) => {
      const allAnnouncements = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const now = new Date();
      
      const scheduledList = [];
      const activeList = [];
      const expiredList = [];
      
      allAnnouncements.forEach(ann => {
        const getSafeDate = (timestamp) => {
          if (!timestamp) return null;
          if (typeof timestamp.toDate === 'function') return timestamp.toDate();
          if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
          return null;
        };

        const scheduledDate = getSafeDate(ann.scheduledAt);
        const expirationDate = getSafeDate(ann.expiresAt);
        
        if (ann.isScheduled && scheduledDate && scheduledDate > now) {
            scheduledList.push(ann);
        } else {
            if (expirationDate && now > expirationDate) {
                expiredList.push(ann);
            } else {
                activeList.push(ann);
            }
        }
      });

      setScheduledAnnouncements(scheduledList);
      setExpiredAnnouncements(expiredList);
      setActiveAnnouncements(activeList);
      setLoading(false);
    });

    const userReadStatusRef = collection(db, 'users', currentUser.uid, 'readAnnouncements');
    const unsubscribeReadStatus = onSnapshot(userReadStatusRef, (snapshot) => {
      const readStatusMap = {};
      snapshot.docs.forEach(doc => {
        readStatusMap[doc.id] = true;
      });
      setReadAnnouncements(readStatusMap);
    });

    return () => {
      unsubscribeChurch();
      unsubscribeAnnouncements();
      unsubscribeReadStatus();
    };
  }, [userProfile, currentUser]);


  useEffect(() => {
    if (loading || !announcementId) return;

    const targetAnnouncement = document.getElementById(`announcement-${announcementId}`);
    if (targetAnnouncement) {
      window.requestAnimationFrame(() => {
        targetAnnouncement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [loading, announcementId, scheduledAnnouncements, activeAnnouncements, expiredAnnouncements]);

  const handleOpenModal = (announcement = null) => {
    const announcementWithDates = announcement ? {
      ...announcement,
      expiresAt: announcement.expiresAt && typeof announcement.expiresAt.toDate === 'function' ? announcement.expiresAt.toDate() : null,
      scheduledAt: announcement.scheduledAt && typeof announcement.scheduledAt.toDate === 'function' ? announcement.scheduledAt.toDate() : null,
    } : null;
    setEditingAnnouncement(announcementWithDates);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAnnouncement(null);
  };

  const handleSaveAnnouncement = async (announcementData, sendNotification) => {
    setLoading(true);
    let announcementId = editingAnnouncement?.id;
    const { selectedFiles, existingAttachments, scheduleTime, ...restOfAnnouncementData } = announcementData;
    
    let finalAttachments = existingAttachments || [];

    try {
      const functions = getFunctions();
      let announcementIdToUse = announcementId || doc(collection(db, 'churches', userProfile.churchId, 'announcements')).id;

      const uploadPromises = selectedFiles.map(async (file) => {
        const path = `churches/${userProfile.churchId}/announcements/${announcementIdToUse}/${file.name}`;
        const url = await uploadFile(file, path);
        return { name: file.name, url, type: file.type };
      });
      const newUploadedAttachments = await Promise.all(uploadPromises);
      finalAttachments = [...finalAttachments, ...newUploadedAttachments];

      const oldAttachmentUrls = editingAnnouncement?.attachments?.map(att => att.url) || [];
      const currentAttachmentUrls = finalAttachments.map(att => att.url);
      const attachmentsToDelete = oldAttachmentUrls.filter(url => !currentAttachmentUrls.includes(url));

      const deletePromises = attachmentsToDelete.map(url => deleteFileByUrl(url));
      await Promise.all(deletePromises);

      const dataToSave = {
        ...restOfAnnouncementData,
        attachments: finalAttachments,
        authorName: `${userProfile.firstName} ${userProfile.lastName}`,
        authorId: currentUser.uid,
        churchId: userProfile.churchId,
      };

      if (scheduleTime && scheduleTime > new Date()) {
        dataToSave.isScheduled = true;
        dataToSave.scheduledAt = Timestamp.fromDate(scheduleTime);
        dataToSave.sendNotificationOnSchedule = sendNotification;
      } else {
        dataToSave.isScheduled = false;
        dataToSave.scheduledAt = null;
        dataToSave.sendNotificationOnSchedule = false;
      }

      const announcementRef = doc(db, 'churches', userProfile.churchId, 'announcements', announcementIdToUse);

      if (editingAnnouncement) {
        dataToSave.updatedAt = serverTimestamp();
        await updateDoc(announcementRef, dataToSave);
      } else {
        dataToSave.createdAt = serverTimestamp();
        await setDoc(announcementRef, dataToSave);
      }

      if (scheduleTime && scheduleTime > new Date()) {
        const scheduleAnnouncement = httpsCallable(functions, 'scheduleAnnouncement');
        await scheduleAnnouncement({
            announcementId: announcementIdToUse,
            churchId: userProfile.churchId,
            scheduleTime: scheduleTime.toISOString(),
            isEditing: !!editingAnnouncement,
        });
        alert("Announcement scheduled successfully!");
      } else {
        if (editingAnnouncement?.isScheduled) {
            const cancelScheduled = httpsCallable(functions, 'cancelScheduledAnnouncement');
            await cancelScheduled({ announcementId: announcementIdToUse });
        }
        
        if (sendNotification) {
            const triggerNotification = httpsCallable(functions, 'triggerAnnouncementNotification');
            await triggerNotification({ churchId: userProfile.churchId, announcementId: announcementIdToUse });
        }
        alert(editingAnnouncement ? "Announcement updated!" : "Announcement created!");
      }
      
      setLoading(false);
      handleCloseModal();
    } catch (error) {
      console.error("Error saving announcement:", error);
      alert(`Failed to save announcement: ${error.message}`);
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      setLoading(true);
      try {
        const functions = getFunctions();
        const annDoc = await getDoc(doc(db, 'churches', userProfile.churchId, 'announcements', id));
        if (annDoc.exists()) {
          const attachmentsToDelete = annDoc.data().attachments || [];
          const deleteStoragePromises = attachmentsToDelete.map(att => deleteFileByUrl(att.url));
          await Promise.all(deleteStoragePromises);
        }
        await deleteDoc(doc(db, 'churches', userProfile.churchId, 'announcements', id));
        
        if (annDoc.data().isScheduled) {
            const cancelScheduled = httpsCallable(functions, 'cancelScheduledAnnouncement');
            await cancelScheduled({ announcementId: id });
        }
        
        alert("Announcement deleted successfully!");
      } catch (error) {
        console.error("Error deleting announcement:", error);
        alert(`Failed to delete announcement: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMarkAsRead = async (announcementId) => {
    if (!currentUser?.uid) return;
    const readDocRef = doc(db, 'users', currentUser.uid, 'readAnnouncements', announcementId);
    try {
      await setDoc(readDocRef, { readAt: serverTimestamp() }, { merge: true });
    } catch (error) {
      console.error("Error marking announcement as read:", error);
    }
  };

  const canCreateAnnouncements = userProfile?.role === 'Leadership' || userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator' || userProfile?.canSendNotifications;

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Announcements</Typography>
        {canCreateAnnouncements && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            New Announcement
          </Button>
        )}
      </Box>

      {canCreateAnnouncements && scheduledAnnouncements.length > 0 && (
        <Paper sx={{ mb: 4, p: 2, backgroundColor: 'warning.light' }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}><ScheduleIcon sx={{ mr: 1 }} /> Scheduled</Typography>
            {scheduledAnnouncements.map(announcement => (
                <AnnouncementCard
                    key={announcement.id}
                    announcement={announcement}
                    userRole={userProfile.role}
                    onEdit={handleOpenModal}
                    onDelete={handleDeleteAnnouncement}
                    isRead={true}
                    onMarkAsRead={() => {}}
                    autoExpand={announcement.id === announcementId}
                />
            ))}
        </Paper>
      )}

      {activeAnnouncements.length === 0 && !loading && (
        <Typography>No active announcements at this time.</Typography>
      )}

      {activeAnnouncements.map(announcement => (
        <AnnouncementCard
          key={announcement.id}
          announcement={announcement}
          userRole={userProfile.role}
          onEdit={handleOpenModal}
          onDelete={handleDeleteAnnouncement}
          isRead={readAnnouncements[announcement.id] || false}
          onMarkAsRead={handleMarkAsRead}
          autoExpand={announcement.id === announcementId}
        />
      ))}

      {canCreateAnnouncements && expiredAnnouncements.length > 0 && (
          <Box mt={4}>
              <Typography variant="h5" gutterBottom>Expired Announcements</Typography>
              <Divider sx={{ mb: 2 }}/>
              {expiredAnnouncements.map(announcement => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  userRole={userProfile.role}
                  onEdit={handleOpenModal}
                  onDelete={handleDeleteAnnouncement}
                  isRead={true}
                  onMarkAsRead={() => {}}
                  autoExpand={announcement.id === announcementId}
                />
              ))}
          </Box>
      )}

      <AnnouncementModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveAnnouncement}
        announcement={editingAnnouncement}
        editorProfile={userProfile}
        isChurchPushNotificationsEnabled={isChurchPushNotificationsEnabled}
      />
    </Container>
  );
}

export default Announcements;